import React from "react";
import DataTable from "react-data-table-component";
import ReactPaginate from "react-paginate";
import moment from "moment";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { Badge, Button, Card, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import { Dna } from "react-loader-spinner";
import { exportHistoryUserJoinExit, getHistoryUserJoinExit } from "stores";
import "./history-user-join-exit.css";

function HistoryUserJoinExit() {
  const auth = useSelector((state) => state.authReducer);
  const allowedRoles = ["super admin", "admin", "hrd"];
  const [dataHistory, setDataHistory] = React.useState([]);
  const [summary, setSummary] = React.useState({ totalJoin: 0, totalExit: 0 });
  const [startDate, setStartDate] = React.useState(
    moment().startOf("month").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = React.useState(moment().format("YYYY-MM-DD"));
  const [type, setType] = React.useState("all");
  const [searchText, setSearchText] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [limit, setLimit] = React.useState(10);
  const [totalDocs, setTotalDocs] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [pagingCounter, setPagingCounter] = React.useState(1);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);

  moment.locale("id");

  const fetchHistory = React.useCallback(
    async (nextPage = page) => {
      if (!allowedRoles.includes(auth.role)) {
        return;
      }

      if (!startDate || !endDate) {
        Swal.fire({
          icon: "warning",
          title: "Tanggal wajib diisi",
          text: "Pilih tanggal mulai dan tanggal sampai terlebih dahulu.",
        });
        return;
      }

      if (moment(startDate).isAfter(moment(endDate))) {
        Swal.fire({
          icon: "warning",
          title: "Tanggal tidak valid",
          text: "Tanggal mulai tidak boleh lebih besar dari tanggal sampai.",
        });
        return;
      }

      setIsLoading(true);
      try {
        const response = await getHistoryUserJoinExit({
          start: startDate,
          end: endDate,
          type,
          cari: searchText,
          page: nextPage + 1,
          limit,
        });

        setDataHistory(response.docs || []);
        setSummary(response.summary || { totalJoin: 0, totalExit: 0 });
        setTotalDocs(response.totalDocs || 0);
        setTotalPages(response.totalPages || 0);
        setPagingCounter(response.pagingCounter || 1);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Gagal memuat data",
          text: error.message || "Terjadi kesalahan saat memuat history user.",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [auth.role, startDate, endDate, type, searchText, page, limit],
  );

  React.useEffect(() => {
    if (allowedRoles.includes(auth.role)) {
      fetchHistory(0);
    }
  }, [auth.role, limit]);

  const handleFilter = () => {
    setPage(0);
    fetchHistory(0);
  };

  const handleClearSearch = () => {
    setSearchText("");
    setPage(0);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleFilter();
    }
  };

  const getExportErrorMessage = async (response, fallbackMessage) => {
    if (!response || !response.status || response.status === 200) {
      return null;
    }

    if (response.data instanceof Blob) {
      const text = await response.data.text();
      try {
        const parsed = JSON.parse(text);
        return parsed.message || fallbackMessage;
      } catch (error) {
        return text || fallbackMessage;
      }
    }

    return response.data && response.data.message
      ? response.data.message
      : fallbackMessage;
  };

  const submitExport = async () => {
    if (!startDate || !endDate) {
      Swal.fire({
        icon: "warning",
        title: "Tanggal wajib diisi",
        text: "Pilih tanggal mulai dan tanggal sampai terlebih dahulu.",
      });
      return;
    }

    if (moment(startDate).isAfter(moment(endDate))) {
      Swal.fire({
        icon: "warning",
        title: "Tanggal tidak valid",
        text: "Tanggal mulai tidak boleh lebih besar dari tanggal sampai.",
      });
      return;
    }

    Swal.fire({
      title: "Konfirmasi Export",
      text: "Export data history join/exit sesuai filter saat ini?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Ya, Export",
      cancelButtonText: "Batal",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        setIsExporting(true);
        try {
          const response = await exportHistoryUserJoinExit({
            start: startDate,
            end: endDate,
            type,
            cari: searchText,
          });
          const errorMessage = await getExportErrorMessage(
            response,
            "Gagal export history join/exit",
          );
          if (errorMessage) {
            throw new Error(errorMessage);
          }

          const url = window.URL.createObjectURL(new Blob([response]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `History Join Exit ${startDate} sampai ${endDate}.xlsx`,
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          Swal.fire({
            icon: "success",
            title: "Data berhasil diexport",
          });
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Terjadi kesalahan",
            text: error.message || "Gagal export history join/exit.",
          });
        } finally {
          setIsExporting(false);
        }
      },
    });
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(value || 0);

  const columns = [
    {
      name: "No",
      width: "76px",
      center: true,
      selector: (row, index) => index + pagingCounter,
    },
    {
      name: "Tanggal",
      minWidth: "130px",
      selector: (row) => moment(row.date).format("DD MMM YYYY"),
      sortable: true,
    },
    {
      name: "Status",
      width: "120px",
      cell: (row) => (
        <Badge
          style={{
            backgroundColor: row.jenis === "join" ? "#E1FAEE" : "#FFE6E6",
            color: row.jenis === "join" ? "#047857" : "#B91C1C",
            borderRadius: 20,
            padding: "8px 12px",
            textTransform: "uppercase",
          }}
        >
          {row.jenis}
        </Badge>
      ),
    },
    {
      name: "User",
      minWidth: "240px",
      cell: (row) => (
        <div>
          <div style={{ fontWeight: 700 }}>{row.nama || "-"}</div>
          <div style={{ color: "#6B7280", fontSize: 12 }}>
            {row.nopeg || "-"} {row.email ? `- ${row.email}` : ""}
          </div>
        </div>
      ),
    },
    {
      name: "Perusahaan",
      minWidth: "220px",
      selector: (row) => row.namaPerusahaan || "-",
    },
    {
      name: "Client",
      minWidth: "200px",
      selector: (row) => row.namaClient || "-",
    },
    {
      name: "Cabang",
      minWidth: "200px",
      selector: (row) => row.namaCabang || "-",
    },
    {
      name: "Status Koperasi",
      minWidth: "150px",
      selector: (row) => row.statusKoperasi || "-",
    },
    {
      name: "Resign",
      width: "110px",
      selector: (row) => row.resign || "-",
    },
    {
      name: "Simpanan Akhir",
      minWidth: "170px",
      right: true,
      selector: (row) => formatCurrency(row.nominalSimpananAkhir),
    },
  ];

  const customStyles = {
    table: {
      style: {
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid #E5E7EB",
      },
    },
    headRow: {
      style: {
        minHeight: "52px",
        backgroundColor: "#7132CA",
      },
    },
    headCells: {
      style: {
        color: "#FFFFFF",
        fontWeight: 700,
        fontSize: 13,
      },
    },
    rows: {
      style: {
        minHeight: "62px",
        fontSize: 13,
      },
    },
  };

  if (!allowedRoles.includes(auth.role)) {
    return (
      <Container fluid>
        <Card>
          <Card.Body style={{ textAlign: "center", padding: 40 }}>
            <h4 style={{ fontWeight: 700 }}>Akses ditolak</h4>
            <p style={{ color: "#6B7280", marginBottom: 0 }}>
              Halaman ini hanya untuk Super Admin, Admin, dan HRD.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row>
        <Col md="12">
          <Card>
            <Card.Header>
              <Card.Title
                as="h3"
                style={{ color: "black", fontWeight: "bold", marginBottom: 6 }}
              >
                History Join & Exit User
              </Card.Title>
              <p style={{ color: "#6B7280", marginBottom: 0 }}>
                Monitoring anggota yang join dan exit berdasarkan data harian.
              </p>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md="4" sm="12" style={{ marginBottom: 12 }}>
                  <Card style={{ borderRadius: 8, border: "1px solid #E5E7EB" }}>
                    <Card.Body>
                      <div style={{ color: "#6B7280", fontWeight: 700 }}>
                        Total Join
                      </div>
                      <div style={{ color: "#047857", fontSize: 30, fontWeight: 800 }}>
                        {summary.totalJoin || 0}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md="4" sm="12" style={{ marginBottom: 12 }}>
                  <Card style={{ borderRadius: 8, border: "1px solid #E5E7EB" }}>
                    <Card.Body>
                      <div style={{ color: "#6B7280", fontWeight: 700 }}>
                        Total Exit
                      </div>
                      <div style={{ color: "#B91C1C", fontSize: 30, fontWeight: 800 }}>
                        {summary.totalExit || 0}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md="4" sm="12" style={{ marginBottom: 12 }}>
                  <Card style={{ borderRadius: 8, border: "1px solid #E5E7EB" }}>
                    <Card.Body>
                      <div style={{ color: "#6B7280", fontWeight: 700 }}>
                        Total Data
                      </div>
                      <div style={{ color: "#111827", fontSize: 30, fontWeight: 800 }}>
                        {totalDocs || 0}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row style={{ alignItems: "flex-end", marginTop: 8 }}>
                <Col lg="2" md="4" sm="12" style={{ marginBottom: 12 }}>
                  <Form.Group style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 700, textTransform: "none" }}>
                      Tanggal Mulai
                    </label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col lg="2" md="4" sm="12" style={{ marginBottom: 12 }}>
                  <Form.Group style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 700, textTransform: "none" }}>
                      Tanggal Sampai
                    </label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col lg="2" md="4" sm="12" style={{ marginBottom: 12 }}>
                  <Form.Group style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 700, textTransform: "none" }}>
                      Jenis
                    </label>
                    <Form.Control
                      as="select"
                      value={type}
                      onChange={(e) => {
                        setType(e.target.value);
                        setPage(0);
                      }}
                    >
                      <option value="all">Semua</option>
                      <option value="join">Join</option>
                      <option value="exit">Exit</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col lg="3" md="8" sm="12" style={{ marginBottom: 12 }}>
                  <Form.Group style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 700, textTransform: "none" }}>
                      Pencarian
                    </label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Cari nama, nopeg, email, perusahaan, client, cabang"
                      />
                      <InputGroup.Append>
                        <InputGroup.Text
                          style={{ cursor: "pointer" }}
                          onClick={handleClearSearch}
                        >
                          <i className="fas fa-times"></i>
                        </InputGroup.Text>
                      </InputGroup.Append>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col lg="3" md="4" sm="12" style={{ marginBottom: 12 }}>
                  <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                    <Button
                      style={{
                        backgroundColor: "#7132CA",
                        border: 0,
                        fontWeight: 700,
                        flex: 1,
                        minWidth: 92,
                      }}
                      onClick={handleFilter}
                    >
                      Filter
                    </Button>
                    <Button
                      disabled={isExporting}
                      style={{
                        backgroundColor: isExporting ? "#9CA3AF" : "#16A34A",
                        border: 0,
                        fontWeight: 700,
                        flex: 1,
                        minWidth: 118,
                      }}
                      onClick={submitExport}
                    >
                      {isExporting ? "Export..." : "Export Excel"}
                    </Button>
                    <Form.Control
                      as="select"
                      value={limit}
                      onChange={(e) => {
                        setPage(0);
                        setLimit(Number(e.target.value));
                      }}
                      style={{ width: 86 }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Form.Control>
                  </div>
                </Col>
              </Row>

              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 240,
                  }}
                >
                  <Dna visible={true} height="80" width="80" ariaLabel="dna-loading" />
                  <p>Loading...</p>
                </div>
              ) : (
                <>
                  <DataTable
                    columns={columns}
                    data={dataHistory}
                    customStyles={customStyles}
                    responsive
                    striped
                    highlightOnHover
                    noDataComponent="Data history join/exit tidak ditemukan"
                  />
                  <Row style={{ alignItems: "center", marginTop: 20 }}>
                    <Col
                      md="6"
                      sm="12"
                      className="history-join-exit-pagination-info"
                    >
                      Menampilkan {dataHistory.length} dari {totalDocs} data
                    </Col>
                    <Col md="6" sm="12">
                      {totalPages > 1 ? (
                        <ReactPaginate
                          breakLabel="..."
                          pageCount={totalPages}
                          marginPagesDisplayed={1}
                          pageRangeDisplayed={2}
                          containerClassName="history-join-exit-pagination"
                          pageClassName="history-join-exit-page"
                          pageLinkClassName="history-join-exit-page-link"
                          previousClassName="history-join-exit-page history-join-exit-nav"
                          previousLinkClassName="history-join-exit-page-link"
                          nextClassName="history-join-exit-page history-join-exit-nav"
                          nextLinkClassName="history-join-exit-page-link"
                          breakClassName="history-join-exit-page history-join-exit-break"
                          breakLinkClassName="history-join-exit-page-link"
                          activeClassName="history-join-exit-active"
                          disabledClassName="history-join-exit-disabled"
                          nextLabel=">"
                          previousLabel="<"
                          forcePage={page}
                          onPageChange={(val) => {
                            setPage(val.selected);
                            fetchHistory(val.selected);
                          }}
                        />
                      ) : null}
                    </Col>
                  </Row>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default HistoryUserJoinExit;
